<section
  class="flex h-full flex-col overflow-hidden bg-white shadow-2xl sm:rounded-2xl"
  role="dialog"
  [attr.aria-label]="'agent-chat.title' | translate"
  data-cy="agent-chat-panel"
>
  <header class="flex items-center gap-1 border-b border-gray-100 px-3 py-2">
    @if (store.view() === 'history') {
      <button
        mat-icon-button
        type="button"
        (click)="store.showChat()"
        [attr.aria-label]="'agent-chat.back' | translate"
        data-cy="agent-chat-back"
      >
        <mat-icon>arrow_back</mat-icon>
      </button>
      <h2 class="grow font-semibold">{{ 'agent-chat.history' | translate }}</h2>
    } @else {
      <h2 class="grow pl-2 font-semibold">{{ 'agent-chat.title' | translate }}</h2>
      <button
        mat-icon-button
        type="button"
        (click)="store.showHistory()"
        [attr.aria-label]="'agent-chat.history' | translate"
        data-cy="agent-chat-history"
      >
        <mat-icon>history</mat-icon>
      </button>
      <button
        mat-icon-button
        type="button"
        (click)="store.startNew()"
        [attr.aria-label]="'agent-chat.new' | translate"
        data-cy="agent-chat-new"
      >
        <mat-icon>edit</mat-icon>
      </button>
    }
    <!-- On a narrow screen the panel already fills the viewport, so there is nothing to enlarge. -->
    <button
      mat-icon-button
      type="button"
      class="max-sm:!hidden"
      (click)="store.toggleExpanded()"
      [attr.aria-label]="(store.expanded() ? 'agent-chat.collapse' : 'agent-chat.expand') | translate"
      [attr.aria-pressed]="store.expanded()"
      data-cy="agent-chat-expand"
    >
      <mat-icon>{{ store.expanded() ? 'close_fullscreen' : 'open_in_full' }}</mat-icon>
    </button>
    <button
      mat-icon-button
      type="button"
      (click)="store.setOpen(false)"
      [attr.aria-label]="'agent-chat.close' | translate"
      data-cy="agent-chat-close"
    >
      <mat-icon>close</mat-icon>
    </button>
  </header>

  @if (store.view() === 'history') {
    <div class="grow overflow-y-auto">
      <app-agent-chat-history
        [conversations]="store.conversations()"
        [loading]="store.loading()"
        (selected)="store.openConversation($event)"
      />
    </div>
  } @else {
    <div #scroller class="grow overflow-y-auto px-4 py-4">
      @if (store.loading()) {
        <p class="text-sm text-gray-500" data-cy="agent-chat-loading">{{ 'common.loading' | translate }}</p>
      } @else {
        @if (!store.messages().length && !store.pending() && store.canSend()) {
          <p class="rounded-2xl bg-gray-100 px-3 py-2 text-sm" data-cy="agent-chat-welcome">
            {{ 'agent-chat.welcome' | translate }}
          </p>
          <div class="mt-3 flex flex-col items-start gap-2" data-cy="agent-chat-starters">
            @for (starter of starters; track starter) {
              <button
                type="button"
                class="rounded-full border border-brand-600 px-3 py-1 text-left text-sm text-brand-600 hover:bg-brand-100"
                (click)="ask(starter)"
                data-cy="agent-chat-starter"
              >
                {{ starter | translate }}
              </button>
            }
          </div>
        }
        <app-agent-message-list [messages]="store.messages()" [pending]="store.pending()" (rated)="rate($event)" />
      }

      @if (store.failure(); as failure) {
        <div class="mt-4 flex flex-col items-end gap-2" data-cy="agent-chat-failure">
          <p
            class="max-w-[85%] whitespace-pre-wrap break-words rounded-2xl rounded-br-sm bg-gray-200 px-3 py-2 text-gray-600"
          >
            {{ failure.content }}
          </p>
          <p class="text-right text-sm text-error-text" data-cy="agent-chat-failure-message">
            {{ failureKey(failure.code) | translate: { seconds: failure.retryAfterSeconds ?? 60 } }}
          </p>
          <button mat-stroked-button type="button" (click)="store.retry()" data-cy="agent-chat-retry">
            {{ 'agent-chat.retry' | translate }}
          </button>
        </div>
      }
    </div>

    @if (store.withTeam()) {
      <p class="border-t border-gray-100 bg-brand-100 px-4 py-2 text-sm" data-cy="agent-chat-waiting">
        <span class="font-semibold">{{ 'agent-chat.waiting.title' | translate }}</span>
        {{ 'agent-chat.waiting.body' | translate }}
      </p>
    } @else if (!store.enabled()) {
      <p class="border-t border-gray-100 bg-gray-100 px-4 py-2 text-sm" data-cy="agent-chat-unavailable">
        {{ 'agent-chat.unavailable' | translate }}
      </p>
    }

    <form class="flex items-end gap-2 border-t border-gray-100 p-3" (ngSubmit)="send()">
      <textarea
        class="min-h-[40px] grow resize-none rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none focus:border-brand-600"
        name="draft"
        rows="1"
        cdkTextareaAutosize
        cdkAutosizeMinRows="1"
        cdkAutosizeMaxRows="4"
        [ngModel]="draft()"
        (ngModelChange)="draft.set($event)"
        (keydown)="onKeydown($event)"
        [disabled]="!store.canSend()"
        [placeholder]="(store.withTeam() ? 'agent-chat.placeholder-team' : 'agent-chat.placeholder') | translate"
        [attr.aria-label]="(store.withTeam() ? 'agent-chat.placeholder-team' : 'agent-chat.placeholder') | translate"
        data-cy="agent-chat-input"
      ></textarea>
      <button
        mat-icon-button
        type="submit"
        [disabled]="!draft().trim() || store.sending() || !store.canSend()"
        [attr.aria-label]="'agent-chat.send' | translate"
        data-cy="agent-chat-send"
      >
        <mat-icon>send</mat-icon>
      </button>
    </form>
  }
</section>
